import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, Users, MousePointerClick, TrendingUp, 
  Edit, Trash2, Plus, ExternalLink, BarChart3,
  LogOut, Search, Filter
} from 'lucide-react';
import { adminGetProducts, adminGetAnalytics, adminUpdateProduct, adminDeleteProduct, adminCreateProduct } from '../api/admin';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const AdminDashboard = () => {
  const [products, setProducts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBrand, setFilterBrand] = useState('all');
  const navigate = useNavigate();

  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [productsRes, analyticsRes] = await Promise.all([
        adminGetProducts(),
        adminGetAnalytics()
      ]);
      setProducts(productsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const handleUpdateProduct = async (productId, data) => {
    try {
      await adminUpdateProduct(productId, data);
      toast.success('Product updated!');
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await adminDeleteProduct(productId);
      toast.success('Product deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleAddProduct = async (data) => {
    try {
      await adminCreateProduct(data);
      toast.success('Product created!');
      setShowAddProduct(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create product');
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.brand.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesBrand = filterBrand === 'all' || p.brand === filterBrand;
    return matchesSearch && matchesBrand;
  });

  const brands = [...new Set(products.map(p => p.brand))];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D2FF00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-xl text-[#D2FF00]">INDIGO</span>
              <span className="font-display text-xl text-white">SQUARE</span>
            </Link>
            <span className="text-white/40">|</span>
            <span className="text-white/60">Admin Panel</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/60 text-sm">{adminUser.email}</span>
            <Button onClick={handleLogout} variant="ghost" className="text-white/60 hover:text-white">
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A0A0A] border border-white/10 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <Package className="text-[#D2FF00]" size={24} />
              <span className="text-white/40 text-sm">Products</span>
            </div>
            <p className="font-display text-4xl">{analytics?.total_products || 0}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#0A0A0A] border border-white/10 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <Users className="text-[#D2FF00]" size={24} />
              <span className="text-white/40 text-sm">Users</span>
            </div>
            <p className="font-display text-4xl">{analytics?.total_users || 0}</p>
            <p className="text-white/40 text-sm mt-1">{analytics?.premium_users || 0} premium</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#0A0A0A] border border-white/10 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <MousePointerClick className="text-[#D2FF00]" size={24} />
              <span className="text-white/40 text-sm">Affiliate Clicks</span>
            </div>
            <p className="font-display text-4xl">{analytics?.total_clicks || 0}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#0A0A0A] border border-white/10 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="text-[#D2FF00]" size={24} />
              <span className="text-white/40 text-sm">Top Brand</span>
            </div>
            <p className="font-display text-2xl">
              {analytics?.clicks_by_brand?.[0]?._id || 'N/A'}
            </p>
            <p className="text-white/40 text-sm mt-1">
              {analytics?.clicks_by_brand?.[0]?.count || 0} clicks
            </p>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-white/10">
          <button
            onClick={() => setActiveTab('products')}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === 'products' 
                ? 'text-[#D2FF00] border-b-2 border-[#D2FF00]' 
                : 'text-white/60 hover:text-white'
            }`}
          >
            Products
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === 'analytics' 
                ? 'text-[#D2FF00] border-b-2 border-[#D2FF00]' 
                : 'text-white/60 hover:text-white'
            }`}
          >
            Analytics
          </button>
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            {/* Actions Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                  <Input
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-default pl-10 w-64"
                  />
                </div>
                <Select value={filterBrand} onValueChange={setFilterBrand}>
                  <SelectTrigger className="w-40 bg-transparent border-white/20">
                    <SelectValue placeholder="Filter brand" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="all">All Brands</SelectItem>
                    {brands.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => setShowAddProduct(true)} className="btn-primary flex items-center gap-2">
                <Plus size={18} />
                ADD PRODUCT
              </Button>
            </div>

            {/* Products Table */}
            <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-white/60 font-medium">Product</th>
                    <th className="text-left p-4 text-white/60 font-medium">Brand</th>
                    <th className="text-left p-4 text-white/60 font-medium">Price</th>
                    <th className="text-left p-4 text-white/60 font-medium">Clicks</th>
                    <th className="text-left p-4 text-white/60 font-medium">Affiliate Link</th>
                    <th className="text-right p-4 text-white/60 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-12 h-12 object-cover bg-[#121212]"
                          />
                          <div>
                            <p className="font-medium truncate max-w-[200px]">{product.name}</p>
                            <p className="text-white/40 text-sm">{product.category}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-white/80">{product.brand}</td>
                      <td className="p-4 text-[#D2FF00]">${product.price}</td>
                      <td className="p-4">
                        <span className="bg-white/10 px-2 py-1 text-sm">
                          {product.click_count || 0}
                        </span>
                      </td>
                      <td className="p-4">
                        <a 
                          href={product.affiliate_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white/40 hover:text-[#D2FF00] truncate max-w-[200px] block text-sm"
                        >
                          {product.affiliate_link.substring(0, 40)}...
                        </a>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setEditingProduct(product)}
                            className="p-2 hover:bg-white/10 transition-colors"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product.id)}
                            className="p-2 hover:bg-red-500/20 text-red-400 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-8">
            {/* Top Products */}
            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <h3 className="font-display text-xl mb-4">TOP CLICKED PRODUCTS</h3>
              <div className="space-y-3">
                {analytics?.top_products?.map((product, i) => (
                  <div key={product._id} className="flex items-center justify-between p-3 bg-white/5">
                    <div className="flex items-center gap-3">
                      <span className="text-[#D2FF00] font-display text-lg">#{i + 1}</span>
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-white/40 text-sm">{product.brand}</p>
                      </div>
                    </div>
                    <span className="text-[#D2FF00]">{product.count} clicks</span>
                  </div>
                ))}
                {(!analytics?.top_products || analytics.top_products.length === 0) && (
                  <p className="text-white/40 text-center py-8">No click data yet</p>
                )}
              </div>
            </div>

            {/* Clicks by Brand */}
            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <h3 className="font-display text-xl mb-4">CLICKS BY BRAND</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {analytics?.clicks_by_brand?.map((brand) => (
                  <div key={brand._id} className="bg-white/5 p-4 text-center">
                    <p className="font-display text-2xl text-[#D2FF00]">{brand.count}</p>
                    <p className="text-white/60 text-sm">{brand._id}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Clicks */}
            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <h3 className="font-display text-xl mb-4">RECENT CLICKS</h3>
              <div className="space-y-2">
                {analytics?.recent_clicks?.map((click) => (
                  <div key={click.id} className="flex items-center justify-between p-3 bg-white/5 text-sm">
                    <div>
                      <p className="font-medium">{click.product_name}</p>
                      <p className="text-white/40">{click.brand}</p>
                    </div>
                    <span className="text-white/40">
                      {new Date(click.created_at).toLocaleString()}
                    </span>
                  </div>
                ))}
                {(!analytics?.recent_clicks || analytics.recent_clicks.length === 0) && (
                  <p className="text-white/40 text-center py-8">No clicks recorded yet</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Product Modal */}
      <Dialog open={!!editingProduct} onOpenChange={() => setEditingProduct(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">EDIT PRODUCT</DialogTitle>
          </DialogHeader>
          {editingProduct && (
            <ProductForm
              product={editingProduct}
              onSubmit={(data) => handleUpdateProduct(editingProduct.id, data)}
              onCancel={() => setEditingProduct(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Product Modal */}
      <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">ADD PRODUCT</DialogTitle>
          </DialogHeader>
          <ProductForm
            onSubmit={handleAddProduct}
            onCancel={() => setShowAddProduct(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Product Form Component
const ProductForm = ({ product, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    brand: product?.brand || '',
    category: product?.category || 'sneakers',
    description: product?.description || '',
    price: product?.price || '',
    original_price: product?.original_price || '',
    image_url: product?.image_url || '',
    affiliate_link: product?.affiliate_link || '',
    sizes: product?.sizes?.join(', ') || '',
    colors: product?.colors?.join(', ') || '',
    tags: product?.tags?.join(', ') || '',
    is_hot: product?.is_hot || false,
    is_limited: product?.is_limited || false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      price: parseFloat(formData.price),
      original_price: parseFloat(formData.original_price),
      sizes: formData.sizes.split(',').map(s => s.trim()).filter(Boolean),
      colors: formData.colors.split(',').map(s => s.trim()).filter(Boolean),
      tags: formData.tags.split(',').map(s => s.trim()).filter(Boolean),
    };
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="input-default"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Brand *</label>
          <Input
            value={formData.brand}
            onChange={(e) => setFormData({...formData, brand: e.target.value})}
            className="input-default"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Category *</label>
        <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
          <SelectTrigger className="bg-transparent border-white/20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            <SelectItem value="sneakers">Sneakers</SelectItem>
            <SelectItem value="apparel">Apparel</SelectItem>
            <SelectItem value="accessories">Accessories</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="input-default min-h-[80px]"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Price *</label>
          <Input
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => setFormData({...formData, price: e.target.value})}
            className="input-default"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Original Price</label>
          <Input
            type="number"
            step="0.01"
            value={formData.original_price}
            onChange={(e) => setFormData({...formData, original_price: e.target.value})}
            className="input-default"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Image URL *</label>
        <Input
          value={formData.image_url}
          onChange={(e) => setFormData({...formData, image_url: e.target.value})}
          className="input-default"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2 text-[#D2FF00]">Affiliate Link *</label>
        <Input
          value={formData.affiliate_link}
          onChange={(e) => setFormData({...formData, affiliate_link: e.target.value})}
          className="input-default border-[#D2FF00]/50"
          placeholder="https://www.nike.com/..."
          required
        />
        <p className="text-white/40 text-xs mt-1">Paste your tracked affiliate link here</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Sizes (comma separated)</label>
          <Input
            value={formData.sizes}
            onChange={(e) => setFormData({...formData, sizes: e.target.value})}
            className="input-default"
            placeholder="7, 8, 9, 10, 11, 12"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Colors (comma separated)</label>
          <Input
            value={formData.colors}
            onChange={(e) => setFormData({...formData, colors: e.target.value})}
            className="input-default"
            placeholder="Black, White, Red"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Tags (comma separated)</label>
        <Input
          value={formData.tags}
          onChange={(e) => setFormData({...formData, tags: e.target.value})}
          className="input-default"
          placeholder="jordan, retro, basketball"
        />
      </div>

      <div className="flex gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_hot}
            onChange={(e) => setFormData({...formData, is_hot: e.target.checked})}
            className="w-4 h-4 accent-[#FF3B30]"
          />
          <span className="text-sm">HOT Drop</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_limited}
            onChange={(e) => setFormData({...formData, is_limited: e.target.checked})}
            className="w-4 h-4 accent-[#D2FF00]"
          />
          <span className="text-sm">LIMITED Edition</span>
        </label>
      </div>

      <div className="flex gap-4 pt-4">
        <Button type="button" onClick={onCancel} className="btn-secondary flex-1">
          CANCEL
        </Button>
        <Button type="submit" className="btn-primary flex-1">
          {product ? 'UPDATE' : 'CREATE'} PRODUCT
        </Button>
      </div>
    </form>
  );
};

export default AdminDashboard;
